import React, { useState } from 'react';
import { supabase } from './supabase';
import { couldNotReach, signInProblem } from './utils/reachability';
import { Mail, Lock, ArrowRight, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import loginHero from './assets/login-form-hero.jpg';
import CipherHero from './components/ui/CipherHero';
import CipherButton from './components/ui/CipherButton';
import { colors, typography, radius, spacing } from './theme';
import { signInWithGoogle } from './utils/googleAuth';

const inputStyle = {
  width: '100%',
  padding: '14px 16px 14px 48px',
  background: colors.surface1,
  border: '1px solid transparent',
  borderRadius: radius.sm,
  color: colors.text,
  fontSize: '15px',
  boxSizing: 'border-box',
  outline: 'none',
};

const fieldLabelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: 600,
  color: colors.textMuted,
  marginBottom: spacing.sm,
};

// Sized to the hero photo's own 3:2 aspect ratio (width = the 520px-max
// column, or the full viewport on phones) so the entire image shows with
// nothing cropped — the CIPHER / "Welcome back." block overlays its lower
// third, which the gradient darkens for contrast. 0.6667 = 1024 / 1536.
// Shared with the ambient backdrop below so both resolve to flat colors.bg
// at the same point — no seam where hero meets page. At ~260–347px this is
// shorter than the old band, so the Log In button stays above the fold.
const HERO_HEIGHT = 'calc(min(100vw, 520px) * 0.6667)';

const BackControl = ({ onClick }) => (
  <button
    onClick={onClick}
    aria-label="Back"
    style={{
      position: 'absolute',
      top: '16px',
      left: '16px',
      zIndex: 2,
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(10, 14, 39, 0.65)',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: radius.pill,
      color: colors.text,
      cursor: 'pointer',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
      backdropFilter: 'blur(4px)',
    }}
  >
    <ArrowLeft size={18} />
  </button>
);

const LoginPage = ({ onLoginSuccess, onSignUp, onHome }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Password reset state
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Google sign-in error:', error);
      setError(error.message || 'Google sign-in failed');
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Sign in with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('Login failed');
      }

      // Get user profile from database
      const profileRead = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authData.user.id)
        .maybeSingle();
      const { data: userData, error: userError } = profileRead;

      // The sign-in itself worked. If the server then goes quiet, the app's own start-up check
      // (onLoginSuccess) says so and keeps trying; an error here would leave a signed-in person
      // on the login page.
      if (userError && !couldNotReach(profileRead)) throw userError;

      // If no profile exists yet, create a minimal one or use auth data
      const userProfile = userData || {
        auth_user_id: authData.user.id,
        email: authData.user.email,
        name: authData.user.user_metadata?.name || authData.user.email?.split('@')[0] || 'User',
      };

      if (onLoginSuccess) {
        onLoginSuccess(userProfile);
      }

    } catch (error) {
      console.error('Login error:', error);
      setError(signInProblem(error, 'Invalid email or password'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setResetSent(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setResetError(signInProblem(error, 'Failed to send reset email'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShowResetForm(false);
    setResetEmail('');
    setResetSent(false);
    setResetError('');
  };

  // Password Reset Form — lighter-touch restyle, no hero (reached only via
  // "Forgot password?", not the primary re-entry moment)
  if (showResetForm) {
    return (
      <div style={{
        minHeight: '100dvh',
        background: colors.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: spacing.xl,
        fontFamily: "'Inter', sans-serif",
      }}>
        <div style={{ width: '100%', maxWidth: '400px' }}>
          <button
            onClick={handleBackToLogin}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              marginBottom: spacing.xl,
              padding: 0,
            }}
          >
            <ArrowLeft size={18} />
            Back to login
          </button>

          <div style={{ marginBottom: spacing['2xl'] }}>
            <h1 style={{ ...typography.screenTitle, color: colors.text, margin: `0 0 ${spacing.sm}` }}>
              Reset password
            </h1>
            <p style={{ ...typography.body, color: colors.textMuted, margin: 0 }}>
              {resetSent
                ? 'Check your email for the reset link'
                : "Enter your email and we'll send you a reset link"
              }
            </p>
          </div>

          {resetSent ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: colors.successDim,
                borderRadius: radius.pill,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: `0 auto ${spacing.lg}`,
              }}>
                <CheckCircle size={28} color={colors.success} />
              </div>
              <p style={{ ...typography.body, color: colors.textMuted, margin: `0 0 ${spacing.xl}`, lineHeight: 1.5 }}>
                We've sent a password reset link to <strong style={{ color: colors.text }}>{resetEmail}</strong>.
                Please check your inbox and spam folder.
              </p>
              <CipherButton onClick={handleBackToLogin}>Return to login</CipherButton>
            </div>
          ) : (
            <form onSubmit={handlePasswordReset}>
              <div style={{ marginBottom: spacing.lg }}>
                <label style={fieldLabelStyle}>Email address</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={18} color={colors.textMuted} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    autoComplete="email"
                    autoFocus
                    style={inputStyle}
                  />
                </div>
              </div>

              {resetError && (
                <div style={{
                  padding: '12px 16px',
                  background: colors.dangerDim,
                  borderRadius: radius.sm,
                  marginBottom: spacing.lg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.md,
                }}>
                  <AlertCircle size={18} color={colors.danger} />
                  <span style={{ fontSize: '14px', color: colors.danger }}>{resetError}</span>
                </div>
              )}

              <CipherButton type="submit" disabled={resetLoading}>
                {resetLoading ? 'Sending...' : 'Send reset link'}
              </CipherButton>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100dvh', background: colors.bg, fontFamily: "'Inter', sans-serif", position: 'relative' }}>
      {/* Ambient backdrop — same photo, blurred + dimmed, fills the frame on
          wide desktop windows so the centered column doesn't float in empty
          navy. Confined to the hero's own height (not the full page) and
          masked to fade out at its own bottom edge, so it resolves to flat
          colors.bg at exactly the point the hero also does — no seam.
          Invisible on mobile widths, where the column is already 100%. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: HERO_HEIGHT,
          overflow: 'hidden',
          zIndex: 0,
          maskImage: 'linear-gradient(to bottom, black 0%, black 55%, transparent 96%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 55%, transparent 96%)',
        }}
      >
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `url(${loginHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 30%',
          filter: 'blur(60px) brightness(0.35) saturate(0.9)',
          transform: 'scale(1.15)',
        }} />
        <div style={{ position: 'absolute', inset: 0, background: colors.bg, opacity: 0.5 }} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
      <div style={{ position: 'relative', maxWidth: '520px', margin: '0 auto' }}>
        <CipherHero image={loginHero} height={HERO_HEIGHT} focalPosition="center center">
          <span style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '4px',
            color: colors.gold,
          }}>
            CIPHER
          </span>
          <h1 style={{ ...typography.display, fontSize: 'clamp(26px, 6vw, 32px)', color: colors.text, margin: 0 }}>
            Welcome back.
          </h1>
          <p style={{ ...typography.body, color: colors.textMuted, margin: 0 }}>
            Your training continues here.
          </p>
        </CipherHero>
        <BackControl onClick={onHome} />
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        padding: `0 ${spacing.xl} ${spacing['3xl']}`,
      }}>
        <div style={{ width: '100%', maxWidth: '400px', marginTop: spacing['2xl'] }}>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: spacing.lg }}>
              <label style={fieldLabelStyle}>Email address</label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} color={colors.textMuted} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="your.email@example.com"
                  required
                  autoComplete="email"
                  style={{
                    ...inputStyle,
                    border: `1px solid ${focusedField === 'email' ? colors.gold : 'transparent'}`,
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: spacing.xl }}>
              <label style={fieldLabelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} color={colors.textMuted} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  style={{
                    ...inputStyle,
                    border: `1px solid ${focusedField === 'password' ? colors.gold : 'transparent'}`,
                  }}
                />
              </div>
            </div>

            {error && (
              <div style={{
                padding: '12px 16px',
                background: colors.dangerDim,
                borderRadius: radius.sm,
                marginBottom: spacing.lg,
                display: 'flex',
                alignItems: 'center',
                gap: spacing.md,
              }}>
                <AlertCircle size={18} color={colors.danger} />
                <span style={{ fontSize: '14px', color: colors.danger }}>{error}</span>
              </div>
            )}

            <CipherButton type="submit" disabled={isLoading}>
              {isLoading ? 'Logging in...' : 'Log in'}
              {!isLoading && <ArrowRight size={20} />}
            </CipherButton>

            <div style={{ textAlign: 'center', marginTop: spacing.lg }}>
              <CipherButton type="button" variant="tertiary" onClick={() => setShowResetForm(true)}>
                Forgot password?
              </CipherButton>
            </div>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: spacing.md, margin: `${spacing.xl} 0` }}>
            <div style={{ flex: 1, height: '1px', background: colors.border }} />
            <span style={{ fontSize: '13px', color: colors.textSubtle }}>or</span>
            <div style={{ flex: 1, height: '1px', background: colors.border }} />
          </div>

          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            style={{
              width: '100%',
              padding: '13px 16px',
              borderRadius: radius.sm,
              background: colors.text,
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.md,
              fontSize: '15px',
              fontWeight: 600,
              color: '#1F1F1F',
              cursor: googleLoading ? 'default' : 'pointer',
              opacity: googleLoading ? 0.7 : 1,
              fontFamily: 'inherit',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.85.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.96 10.71a5.4 5.4 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z"/>
            </svg>
            {googleLoading ? 'Connecting…' : 'Continue with Google'}
          </button>

          <div style={{ textAlign: 'center', marginTop: spacing.xl, borderTop: `1px solid ${colors.border}`, paddingTop: spacing.xl }}>
            <p style={{ fontSize: '14px', color: colors.textSubtle }}>
              Don't have an account?{' '}
              <button
                onClick={() => onSignUp && onSignUp()}
                style={{ background: 'none', border: 'none', color: colors.textMuted, fontSize: '14px', fontWeight: 600, cursor: 'pointer', padding: 0 }}
              >
                Sign up
              </button>
            </p>
            <p style={{ fontSize: '12px', color: colors.textSubtle, marginTop: spacing.md, lineHeight: '1.5' }}>
              Client signup only. Your account will need approval.
              <br />
              Coach/Admin accounts are created by administrators.
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default LoginPage;
